import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock } from 'lucide-react';
import { getRecentBlogPosts, BlogPost } from '../lib/supabase';
import { motion } from 'framer-motion';

export function BlogSection() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPosts() {
      try {
        const recentPosts = await getRecentBlogPosts(3);
        setPosts(recentPosts);
      } catch (error) {
        // Silently handle the error - don't show console errors for missing table
      } finally {
        setLoading(false);
      }
    }

    fetchPosts();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min read`;
  };

  if (loading || posts.length === 0) {
    return null; // Don't show the section if no posts or still loading
  }

  return (
    <section className="py-24 md:py-32 bg-zinc-800/30">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="section-subheading">Blog</h2>
          <h3 className="section-heading mb-6 relative">
            Latest Insights & Tips
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "200px" }}
              transition={{ duration: 1, delay: 0.5 }}
              viewport={{ once: true }}
              className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent"
            />
          </h3>
          <p className="text-zinc-400 max-w-2xl mx-auto">
            Stay informed with expert advice, industry insights, and the latest trends 
            in construction and home renovation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="bg-zinc-800/50 rounded-xl overflow-hidden hover:bg-zinc-700/50 transition-all duration-300 group"
            >
              {post.image_url && (
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src={post.image_url} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-zinc-400 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(post.published_at || post.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    <span>{getReadingTime(post.content)}</span>
                  </div>
                </div>
                
                <h4 className="text-lg font-light text-zinc-100 mb-3 group-hover:text-yellow-400 transition-colors line-clamp-2">
                  {post.title}
                </h4>
                
                <p className="text-zinc-400 mb-4 line-clamp-2 text-sm">
                  {post.description}
                </p>
                
                <Link 
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-2 text-yellow-400 hover:gap-4 transition-all duration-300 group-hover:text-yellow-300"
                >
                  <span className="text-sm tracking-wider">Read More</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link 
            to="/blog"
            className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900 hover:bg-yellow-500 transition-all duration-300 relative overflow-hidden shadow-lg hover:shadow-xl"
          >
            <span className="text-sm tracking-wider relative z-10">View All Posts</span>
            <ArrowRight className="h-4 w-4 transition-transform hover:translate-x-1 relative z-10" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}